import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Store, Package, BarChart3, Megaphone, MapPin, Plus, Edit, Trash2, 
  Eye, MousePointer, Users, DollarSign, TrendingUp, Settings, Image,
  Search, Filter, ChevronRight, ExternalLink, Play, Pause, Target,
  Layers, Globe, Clock, Calendar, CheckCircle, AlertCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SocyyShop = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [shop, setShop] = useState(null);
  const [hasShop, setHasShop] = useState(false);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [adConfig, setAdConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  
  // Modals
  const [showCreateShop, setShowCreateShop] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const getToken = () => localStorage.getItem('socyy_token');

  const fetchShop = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/socyyshop/my-shop`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.success) {
        setShop(data.shop);
        setHasShop(data.has_shop);
      }
    } catch (err) {
      console.error('Error fetching shop:', err);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    if (!hasShop) return;
    try {
      const res = await fetch(`${API}/api/socyyshop/products`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.success) {
        setProducts(data.products);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  }, [hasShop]);

  const fetchAnalytics = useCallback(async () => {
    if (!hasShop) return;
    try {
      const res = await fetch(`${API}/api/socyyshop/analytics?days=30`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  }, [hasShop]);

  const fetchCampaigns = useCallback(async () => {
    if (!hasShop) return;
    try {
      const res = await fetch(`${API}/api/socyyshop/ads/campaigns`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.success) {
        setCampaigns(data.campaigns);
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  }, [hasShop]);

  const fetchAdConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/socyyshop/ads/config`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.success) {
        setAdConfig(data.config);
      }
    } catch (err) {
      console.error('Error fetching ad config:', err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/socyyshop/categories`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.categories) {
        setCategories(data.categories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('socyy_user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    const init = async () => {
      setLoading(true);
      await fetchCategories();
      await fetchShop();
      await fetchAdConfig();
      setLoading(false);
    };
    init();
  }, [fetchShop, fetchCategories, fetchAdConfig]);

  useEffect(() => {
    if (hasShop) {
      fetchProducts();
      fetchAnalytics();
      fetchCampaigns();
    }
  }, [hasShop, fetchProducts, fetchAnalytics, fetchCampaigns]);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'advertising', label: 'Advertising', icon: Megaphone },
    { id: 'locale', label: 'Locale Map', icon: MapPin },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Create Shop Modal
  const CreateShopModal = () => {
    const [formData, setFormData] = useState({
      business_name: '',
      description: '',
      category: 'other',
      address: '',
      phone: '',
      email: '',
      website: ''
    });
    const [creating, setCreating] = useState(false);

    const handleCreate = async (e) => {
      e.preventDefault();
      setCreating(true);
      try {
        const res = await fetch(`${API}/api/socyyshop/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify(formData)
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Shop created successfully!');
          setShop(data.shop);
          setHasShop(true);
          setShowCreateShop(false);
        } else {
          toast.error(data.detail || 'Failed to create shop');
        }
      } catch (err) {
        toast.error('Error creating shop');
      }
      setCreating(false);
    };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-zinc-900 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-zinc-800">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Store className="w-6 h-6 text-emerald-500" />
              Create Your SocyyShop
            </h2>
            <p className="text-zinc-400 text-sm mt-1">Set up your business presence on Socyy</p>
          </div>
          
          <form onSubmit={handleCreate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Business Name *</label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Your Business Name"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500 resize-none"
                rows={3}
                placeholder="Tell customers about your business..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
                placeholder="123 Main St, City, State"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
                  placeholder="contact@business.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Website</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
                placeholder="https://yourwebsite.com"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowCreateShop(false)}
                className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !formData.business_name}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Shop'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Add Product Modal
  const AddProductModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      description: '',
      price: '',
      sale_price: '',
      category: 'other',
      images: [],
      inventory_count: '',
      is_service: false,
      tags: ''
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
      e.preventDefault();
      setSaving(true);
      try {
        const payload = {
          ...formData,
          price: parseFloat(formData.price) || 0,
          sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
          inventory_count: parseInt(formData.inventory_count) || 0,
          tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
        };
        
        const res = await fetch(`${API}/api/socyyshop/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Product added!');
          fetchProducts();
          setShowAddProduct(false);
        } else {
          toast.error(data.detail || 'Failed to add product');
        }
      } catch (err) {
        toast.error('Error adding product');
      }
      setSaving(false);
    };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-zinc-900 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-zinc-800">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Package className="w-6 h-6 text-blue-500" />
              Add Product
            </h2>
          </div>
          
          <form onSubmit={handleSave} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Product Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Product Name"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 resize-none"
                rows={3}
                placeholder="Product description..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Price ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Sale Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Inventory</label>
                <input
                  type="number"
                  value={formData.inventory_count}
                  onChange={(e) => setFormData({ ...formData, inventory_count: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Tags (comma separated)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                placeholder="tag1, tag2, tag3"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_service"
                checked={formData.is_service}
                onChange={(e) => setFormData({ ...formData, is_service: e.target.checked })}
                className="w-4 h-4 rounded bg-zinc-800 border-zinc-700 text-blue-500"
              />
              <label htmlFor="is_service" className="text-sm text-zinc-300">This is a service (not a physical product)</label>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowAddProduct(false)}
                className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !formData.name || !formData.price}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add Product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Create Campaign Modal
  const CreateCampaignModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      objective: 'awareness',
      bid_type: 'cpm',
      bid_amount: '',
      daily_budget: '',
      placements: ['feed'],
      headline: '',
      description: '',
      image_url: '',
      destination_url: '',
      cta_text: 'Learn More'
    });
    const [creating, setCreating] = useState(false);

    const handleCreate = async (e) => {
      e.preventDefault();
      setCreating(true);
      try {
        const payload = {
          ...formData,
          bid_amount: parseFloat(formData.bid_amount),
          daily_budget: parseFloat(formData.daily_budget)
        };
        
        const res = await fetch(`${API}/api/socyyshop/ads/campaigns`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Campaign created! It will be reviewed shortly.');
          fetchCampaigns();
          setShowCreateCampaign(false);
        } else {
          toast.error(data.detail || 'Failed to create campaign');
        }
      } catch (err) {
        toast.error('Error creating campaign');
      }
      setCreating(false);
    };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-zinc-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-zinc-800">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-purple-500" />
              Create Ad Campaign
            </h2>
            <p className="text-zinc-400 text-sm mt-1">Auction-based advertising - compete for ad placements</p>
          </div>
          
          <form onSubmit={handleCreate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Campaign Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                placeholder="Summer Sale Campaign"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Objective</label>
                <select
                  value={formData.objective}
                  onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                >
                  <option value="awareness">Brand Awareness</option>
                  <option value="traffic">Drive Traffic</option>
                  <option value="conversions">Conversions</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Bid Type</label>
                <select
                  value={formData.bid_type}
                  onChange={(e) => setFormData({ ...formData, bid_type: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                >
                  <option value="cpm">CPM (per 1000 views)</option>
                  <option value="cpc">CPC (per click)</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  Bid Amount ($) * 
                  <span className="text-zinc-500 text-xs ml-1">
                    (min: ${formData.bid_type === 'cpm' ? adConfig?.min_bid_cpm : adConfig?.min_bid_cpc})
                  </span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={formData.bid_type === 'cpm' ? adConfig?.min_bid_cpm : adConfig?.min_bid_cpc}
                  value={formData.bid_amount}
                  onChange={(e) => setFormData({ ...formData, bid_amount: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                  placeholder={formData.bid_type === 'cpm' ? '2.00' : '0.25'}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  Daily Budget ($) *
                  <span className="text-zinc-500 text-xs ml-1">(min: ${adConfig?.min_daily_budget})</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min={adConfig?.min_daily_budget}
                  max={adConfig?.max_daily_budget}
                  value={formData.daily_budget}
                  onChange={(e) => setFormData({ ...formData, daily_budget: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                  placeholder="50.00"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Placements</label>
              <div className="flex flex-wrap gap-2">
                {adConfig?.placements?.map(p => (
                  <label key={p} className="flex items-center gap-2 bg-zinc-800 px-3 py-2 rounded-lg cursor-pointer hover:bg-zinc-700">
                    <input
                      type="checkbox"
                      checked={formData.placements.includes(p)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, placements: [...formData.placements, p] });
                        } else {
                          setFormData({ ...formData, placements: formData.placements.filter(x => x !== p) });
                        }
                      }}
                      className="w-4 h-4 rounded bg-zinc-700 border-zinc-600 text-purple-500"
                    />
                    <span className="text-sm text-zinc-300 capitalize">{p.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-zinc-300 mb-3">Ad Creative</h3>
              
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Headline *</label>
                <input
                  type="text"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                  placeholder="Grab attention with your headline"
                  maxLength={60}
                  required
                />
              </div>
              
              <div className="mt-3">
                <label className="block text-sm font-medium text-zinc-300 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500 resize-none"
                  rows={2}
                  placeholder="Tell people what you're offering"
                  maxLength={150}
                  required
                />
              </div>
              
              <div className="mt-3">
                <label className="block text-sm font-medium text-zinc-300 mb-1">Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                  placeholder="https://example.com/ad-image.jpg"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Destination URL *</label>
                  <input
                    type="url"
                    value={formData.destination_url}
                    onChange={(e) => setFormData({ ...formData, destination_url: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                    placeholder="https://yoursite.com/landing"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">CTA Button</label>
                  <select
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Learn More">Learn More</option>
                    <option value="Shop Now">Shop Now</option>
                    <option value="Sign Up">Sign Up</option>
                    <option value="Get Offer">Get Offer</option>
                    <option value="Contact Us">Contact Us</option>
                    <option value="Book Now">Book Now</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-4 mt-4">
              <p className="text-xs text-zinc-400">
                <span className="text-yellow-500">💡 Tip:</span> Higher bids increase your chances of winning ad auctions. 
                Platform fee is {adConfig?.platform_fee_percent}% of your spend.
              </p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowCreateCampaign(false)}
                className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !formData.name || !formData.bid_amount || !formData.daily_budget}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // No Shop State
  const NoShopState = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20">
        <Store className="w-12 h-12 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Welcome to SocyyShop</h2>
      <p className="text-zinc-400 max-w-md mb-6">
        Your business suite for managing products, running ads, and connecting with local customers through Locale.
      </p>
      <button
        onClick={() => setShowCreateShop(true)}
        className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold flex items-center gap-2 transition-colors"
        data-testid="create-shop-btn"
      >
        <Plus className="w-5 h-5" />
        Create Your Shop
      </button>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12 max-w-3xl">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-left">
          <Package className="w-8 h-8 text-blue-500 mb-2" />
          <h3 className="font-semibold text-white mb-1">Product Catalog</h3>
          <p className="text-sm text-zinc-400">Showcase your products and services to Socyy users</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-left">
          <Megaphone className="w-8 h-8 text-purple-500 mb-2" />
          <h3 className="font-semibold text-white mb-1">Auction-Based Ads</h3>
          <p className="text-sm text-zinc-400">Compete for ad placements with Meta-style bidding</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-left">
          <MapPin className="w-8 h-8 text-emerald-500 mb-2" />
          <h3 className="font-semibold text-white mb-1">Locale Integration</h3>
          <p className="text-sm text-zinc-400">Appear on the map and reach local customers</p>
        </div>
      </div>
    </div>
  );

  // Dashboard Tab
  const DashboardTab = () => (
    <div className="space-y-6">
      {/* Shop Header */}
      <div className="bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-800/50 rounded-2xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center text-2xl font-bold text-white">
              {shop?.business_name?.charAt(0) || 'S'}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{shop?.business_name}</h2>
              <p className="text-emerald-300/80 text-sm">{categories.find(c => c.id === shop?.category)?.name || shop?.category}</p>
              {shop?.address && (
                <p className="text-zinc-400 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" /> {shop?.address}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setActiveTab('settings')}
            className="px-3 py-1.5 bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-300 rounded-lg text-sm flex items-center gap-1"
          >
            <Edit className="w-4 h-4" /> Edit
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
            <Eye className="w-4 h-4" /> Views
          </div>
          <p className="text-2xl font-bold text-white">{analytics?.summary?.total_views?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
            <MousePointer className="w-4 h-4" /> Clicks
          </div>
          <p className="text-2xl font-bold text-white">{analytics?.summary?.total_clicks?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
            <Users className="w-4 h-4" /> Followers
          </div>
          <p className="text-2xl font-bold text-white">{analytics?.summary?.total_followers?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
            <TrendingUp className="w-4 h-4" /> CTR
          </div>
          <p className="text-2xl font-bold text-white">{analytics?.summary?.click_through_rate || 0}%</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => setShowAddProduct(true)}
          className="flex items-center gap-3 p-4 bg-blue-900/20 border border-blue-800/50 rounded-xl hover:bg-blue-900/30 transition-colors text-left"
        >
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Plus className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-white">Add Product</p>
            <p className="text-sm text-zinc-400">{products.length} products in catalog</p>
          </div>
        </button>
        
        <button
          onClick={() => setShowCreateCampaign(true)}
          className="flex items-center gap-3 p-4 bg-purple-900/20 border border-purple-800/50 rounded-xl hover:bg-purple-900/30 transition-colors text-left"
        >
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-white">Create Ad Campaign</p>
            <p className="text-sm text-zinc-400">{campaigns.length} active campaigns</p>
          </div>
        </button>
        
        <button
          onClick={() => navigate('/locale')}
          className="flex items-center gap-3 p-4 bg-emerald-900/20 border border-emerald-800/50 rounded-xl hover:bg-emerald-900/30 transition-colors text-left"
        >
          <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-white">View on Locale</p>
            <p className="text-sm text-zinc-400">Your map presence</p>
          </div>
        </button>
      </div>

      {/* Recent Products */}
      {products.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white">Recent Products</h3>
            <button
              onClick={() => setActiveTab('products')}
              className="text-sm text-emerald-500 hover:text-emerald-400 flex items-center gap-1"
            >
              View all <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {products.slice(0, 4).map(product => (
              <div key={product.product_id} className="bg-zinc-800 rounded-lg p-3">
                <div className="aspect-square bg-zinc-700 rounded-lg mb-2 flex items-center justify-center">
                  {product.thumbnail ? (
                    <img src={product.thumbnail} alt={product.name} className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <Package className="w-8 h-8 text-zinc-500" />
                  )}
                </div>
                <p className="text-sm font-medium text-white truncate">{product.name}</p>
                <p className="text-sm text-emerald-500">${product.price}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Products Tab
  const ProductsTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Product Catalog</h2>
        <button
          onClick={() => setShowAddProduct(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>
      
      {products.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <Package className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No products yet</p>
          <button
            onClick={() => setShowAddProduct(true)}
            className="mt-3 text-blue-500 hover:text-blue-400 text-sm"
          >
            Add your first product
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(product => (
            <div key={product.product_id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-colors">
              <div className="aspect-video bg-zinc-800 flex items-center justify-center">
                {product.thumbnail ? (
                  <img src={product.thumbnail} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-12 h-12 text-zinc-600" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-white">{product.name}</h3>
                  <span className={`px-2 py-0.5 text-xs rounded ${product.is_available ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                    {product.is_available ? 'Available' : 'Unavailable'}
                  </span>
                </div>
                <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{product.description || 'No description'}</p>
                <div className="flex items-center justify-between">
                  <div>
                    {product.sale_price ? (
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-emerald-500">${product.sale_price}</span>
                        <span className="text-sm text-zinc-500 line-through">${product.price}</span>
                      </div>
                    ) : (
                      <span className="text-lg font-bold text-white">${product.price}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Eye className="w-3 h-3" /> {product.views_count}
                    <MousePointer className="w-3 h-3 ml-2" /> {product.clicks_count}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Advertising Tab
  const AdvertisingTab = () => {
    const toggleCampaignStatus = async (campaignId, currentStatus) => {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      try {
        const res = await fetch(`${API}/api/socyyshop/ads/campaigns/${campaignId}/status?status=${newStatus}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        const data = await res.json();
        if (data.success) {
          toast.success(`Campaign ${newStatus}`);
          fetchCampaigns();
        }
      } catch (err) {
        toast.error('Failed to update campaign');
      }
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Advertising</h2>
            <p className="text-sm text-zinc-400">Auction-based ads like Meta/Instagram</p>
          </div>
          <button
            onClick={() => setShowCreateCampaign(true)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-4 h-4" /> Create Campaign
          </button>
        </div>

        {/* Ad Account Balance */}
        <div className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-800/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-300">Ad Account Balance</p>
              <p className="text-2xl font-bold text-white">${(shop?.ad_account_balance || 0).toFixed(2)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-purple-300">Total Spend</p>
              <p className="text-lg font-semibold text-white">${(shop?.ad_spend_total || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Campaigns List */}
        {campaigns.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <Megaphone className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No campaigns yet</p>
            <button
              onClick={() => setShowCreateCampaign(true)}
              className="mt-3 text-purple-500 hover:text-purple-400 text-sm"
            >
              Create your first campaign
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {campaigns.map(campaign => (
              <div key={campaign.campaign_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{campaign.name}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        campaign.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                        campaign.status === 'paused' ? 'bg-yellow-500/20 text-yellow-400' :
                        campaign.status === 'pending_approval' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {campaign.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400 capitalize">{campaign.objective} • {campaign.bid_type.toUpperCase()}</p>
                  </div>
                  {(campaign.status === 'active' || campaign.status === 'paused') && (
                    <button
                      onClick={() => toggleCampaignStatus(campaign.campaign_id, campaign.status)}
                      className={`p-2 rounded-lg ${campaign.status === 'active' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-emerald-500/20 text-emerald-400'}`}
                    >
                      {campaign.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
                  <div className="bg-zinc-800 rounded-lg p-2">
                    <p className="text-xs text-zinc-400">Impressions</p>
                    <p className="font-semibold text-white">{campaign.metrics?.impressions?.toLocaleString() || 0}</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-2">
                    <p className="text-xs text-zinc-400">Clicks</p>
                    <p className="font-semibold text-white">{campaign.metrics?.clicks?.toLocaleString() || 0}</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-2">
                    <p className="text-xs text-zinc-400">CTR</p>
                    <p className="font-semibold text-white">{campaign.metrics?.ctr || 0}%</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-2">
                    <p className="text-xs text-zinc-400">Spend</p>
                    <p className="font-semibold text-white">${(campaign.metrics?.spend || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-zinc-800 rounded-lg p-2">
                    <p className="text-xs text-zinc-400">Daily Budget</p>
                    <p className="font-semibold text-white">${campaign.daily_budget}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Locale Tab
  const LocaleTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Locale Map Presence</h2>
          <p className="text-sm text-zinc-400">Your shop appears on Locale maps for local discovery</p>
        </div>
        <button
          onClick={() => navigate('/locale')}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center gap-2"
        >
          <ExternalLink className="w-4 h-4" /> View Locale
        </button>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{shop?.business_name}</h3>
            <p className="text-sm text-zinc-400">{shop?.address || 'No address set'}</p>
          </div>
        </div>
        
        {shop?.latitude && shop?.longitude ? (
          <div className="bg-zinc-800 rounded-lg p-4">
            <p className="text-sm text-zinc-400 mb-2">Location Coordinates</p>
            <p className="text-white font-mono">{shop.latitude.toFixed(6)}, {shop.longitude.toFixed(6)}</p>
            <p className="text-xs text-emerald-500 mt-2">✓ Your shop is visible on the Locale map</p>
          </div>
        ) : (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-yellow-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Add an address in Settings to appear on the map
            </p>
          </div>
        )}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="font-semibold text-white mb-4">Locale Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="font-medium text-white">Map Discovery</p>
              <p className="text-sm text-zinc-400">Users can find your shop on the Locale map</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="font-medium text-white">Product Gallery</p>
              <p className="text-sm text-zinc-400">Showcase products to nearby users</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="font-medium text-white">Local Deals</p>
              <p className="text-sm text-zinc-400">Post deals visible to users in your area</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="font-medium text-white">Promoted Placement</p>
              <p className="text-sm text-zinc-400">Boost visibility with locale_map ads</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Settings Tab
  const SettingsTab = () => {
    const [formData, setFormData] = useState({
      business_name: shop?.business_name || '',
      description: shop?.description || '',
      category: shop?.category || 'other',
      address: shop?.address || '',
      phone: shop?.phone || '',
      email: shop?.email || '',
      website: shop?.website || ''
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async (e) => {
      e.preventDefault();
      setSaving(true);
      try {
        const res = await fetch(`${API}/api/socyyshop/my-shop`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify(formData)
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Shop updated!');
          setShop(data.shop);
        } else {
          toast.error(data.detail || 'Failed to update');
        }
      } catch (err) {
        toast.error('Error updating shop');
      }
      setSaving(false);
    };

    return (
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-white">Shop Settings</h2>
        
        <form onSubmit={handleSave} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Business Name</label>
            <input
              type="text"
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500 resize-none"
              rows={3}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
              placeholder="123 Main St, City, State"
            />
            <p className="text-xs text-zinc-500 mt-1">This address will be geocoded for the Locale map</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Website</label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          
          <div className="pt-4">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="socyyshop-page">
      {/* Header */}
      <header className="bg-zinc-950 border-b border-zinc-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/feed')} className="text-zinc-400 hover:text-white">
              ←
            </button>
            <div className="flex items-center gap-2">
              <Store className="w-6 h-6 text-emerald-500" />
              <h1 className="text-xl font-bold text-white">SocyyShop</h1>
            </div>
          </div>
          
          {hasShop && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-zinc-400 hidden md:block">{user?.email}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {!hasShop ? (
          <NoShopState />
        ) : (
          <div className="flex flex-col md:flex-row gap-6">
            {/* Sidebar Tabs */}
            <nav className="md:w-56 flex-shrink-0">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-emerald-600 text-white'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </nav>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {activeTab === 'dashboard' && <DashboardTab />}
              {activeTab === 'products' && <ProductsTab />}
              {activeTab === 'advertising' && <AdvertisingTab />}
              {activeTab === 'locale' && <LocaleTab />}
              {activeTab === 'settings' && <SettingsTab />}
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {showCreateShop && <CreateShopModal />}
      {showAddProduct && <AddProductModal />}
      {showCreateCampaign && <CreateCampaignModal />}
    </div>
  );
};

export default SocyyShop;
